'use client';

import { useCallback, useEffect, useState } from 'react';

import { auth } from '../firebase/auth';
import { authState$ } from './authState';

/**
 * Subscribe to the current Firebase ID token. Re-fires when the user
 * signs in/out or the SDK silently refreshes the JWT. The returned
 * `refresh` forces a fresh token via `getIdToken(true)` — call it
 * when a long-lived consumer (e.g. a socket connection) needs a
 * known-good token after a backend reject.
 */
export function useAuthToken(): readonly [
  string | undefined,
  () => Promise<string | undefined>,
] {
  const [token, setToken] = useState<string | undefined>();

  useEffect(() => {
    const sub = authState$.subscribe(user => {
      if (!user) {
        setToken(undefined);
        return;
      }
      user
        .getIdToken()
        .then(next => setToken(next))
        .catch(error => {
          console.error("error get current user's auth token:", error);
        });
    });
    return () => sub.unsubscribe();
  }, []);

  const refresh = useCallback(async (): Promise<string | undefined> => {
    const user = auth().currentUser;
    if (!user) return undefined;
    const next = await user.getIdToken(true);
    setToken(next);
    return next;
  }, []);

  return [token, refresh] as const;
}
